import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Fade } from 'react-awesome-reveal';
import { Icon } from '../Icon';
import styles from './Input.css';

const cx = classnames.bind(styles);

function Input({
    value,
    placeholder,
    errorText,
    defaultValue,
    name,
    type,
    inputProps,
    className,
    label,
    onChange,
    onBlur,
    disabled,
    onKeyDown,
    onClear
}) {
    const inputRef = useRef();
    const isClearEnabled = Boolean(onClear) && !errorText && Boolean(value);

    const handleChange = useCallback(e => {        
        if (onChange) {
            onChange(name, e.target.value);
        }
    }, [onChange, name]);

    const handleBlur = e => {
        if (onBlur) {
            onBlur(e.target.value);
        }
    };

    const clear = () => {
        handleChange({target: {value: ''}});
        onClear?.();

        inputRef.current.focus();
    };

    const renderInput = () => {
        const inputClasses = cx({
            input: true,
            error: Boolean(errorText),
            withIndent: isClearEnabled || Boolean(errorText)
        });

        return (
            <input
                className    = {inputClasses}
                defaultValue = {defaultValue}
                value        = {value}
                name         = {name}
                type         = {type || 'text'}
                placeholder  = {placeholder}
                onChange     = {handleChange}
                onBlur       = {handleBlur}
                onKeyDown    = {onKeyDown}
                autoComplete = 'off'
                disabled     = {disabled}
                ref          = {inputRef}
                data-1p-ignore
                {...inputProps}
            />
        );
    }

    const classes = cx({
        root: true,
        [className]: Boolean(className)
    });

    return (
        <div className={classes}>
           {Boolean(label) && (
                <label className={styles.label}>
                    {label}
                </label>
            )}
            <div className={styles.container}>
                {renderInput()}
                {Boolean(errorText) && (
                    <Icon className={styles.icon} name='alertTriangle' />
                )}
                {isClearEnabled && (
                    <Icon className={cx('icon', 'iconClear')} name='close' onClick={clear} />
                )}
            </div>
            {Boolean(errorText) && (
                <Fade triggerOnce duration={300}>
                    <p className={styles.alert}>{errorText}</p>
                </Fade>
            )}
        </div>
    );
}

Input.propTypes = {
    type         : PropTypes.string,
    value        : PropTypes.string,
    name         : PropTypes.string,
    defaultValue : PropTypes.string,
    placeholder  : PropTypes.string,
    label        : PropTypes.string,
    errorText    : PropTypes.string,
    onChange     : PropTypes.func,
    onBlur       : PropTypes.func,
    onKeyDown    : PropTypes.func,
    className    : PropTypes.string,
    inputProps   : PropTypes.object,
    onClear    : PropTypes.func
};

export {
    Input
};
